import React, { useState, useEffect } from 'react';
import { Search, Filter, AlertCircle, UserPlus, LayoutGrid, Table2 } from 'lucide-react';
import UserCard from './UserCard';
import UserVerifyTable from './UserVerifyTable';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import UserForm from './UserForm';
import { debugLog } from '../../utils/logger';

const UserList = ({ onEditUser, onCreateWorkout }) => {
    const { getAuthenticatedApi, isAdmin } = useAuth();
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        role: 'gym', // Default: mostra solo palestre
        status: 'all',
        showOptionalRoles: {
            standalone: false,
            trainer: false,
            user: false
        }
    });
    const [showUserForm, setShowUserForm] = useState(false);
    const [viewMode, setViewMode] = useState('verify');

    useEffect(() => {
        let isMounted = true;
        const api = getAuthenticatedApi();

        const fetchUsers = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await api.get('/users.php');

                console.log('API Response:', response.data);

                if (!response.data) {
                    throw new Error('Risposta API vuota');
                }

                if (typeof response.data === 'string' && response.data.includes('<br />')) {
                    console.error('Received HTML error instead of JSON:', response.data);
                    throw new Error('Errore interno del server PHP');
                }

                const usersArray = Array.isArray(response.data) 
                    ? response.data 
                    : (response.data.error 
                        ? [] 
                        : [response.data]); 
                
                if (!isMounted) return;

                setUsers(usersArray);
                
                if (usersArray && usersArray.length > 0) {
                    const usersWithWorkouts = await Promise.all(
                        usersArray.map(async (user) => {
                            if (!user || !user.id) {
                                console.warn("Invalid user object", user);
                                return user;
                            }
                            
                            try {
                                const workoutsResponse = await api.get(`/user_assignments.php?user_id=${user.id}`);
                                return {
                                    ...user,
                                    workouts: workoutsResponse.data || []
                                };
                            } catch (workoutError) {
                                console.warn(`Error fetching workouts for user ${user.id}:`, workoutError);
                                return {
                                    ...user,
                                    workouts: []
                                };
                            }
                        })
                    );

                    if (isMounted) {
                        setUsers(usersWithWorkouts);
                    }
                }
            } catch (err) {
                if (isMounted) {
                    console.error('Error fetching users:', err);
                    setError(`Errore nel caricamento degli utenti: ${err.message}`);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchUsers();

        return () => {
            isMounted = false;
        };
    }, [getAuthenticatedApi]);

    const handleDeleteUsers = async (selectedUsers) => {
        const api = getAuthenticatedApi();
        const failed = [];
        const deletedIds = new Set();

        for (const user of selectedUsers) {
            try {
                await api.delete(`/users.php?id=${user.id}`);
                deletedIds.add(String(user.id));
            } catch (err) {
                console.error('Error deleting user:', user.id, err);
                failed.push(user.username || `#${user.id}`);
            }
        }

        if (deletedIds.size) {
            setUsers((prev) => prev.filter((user) => !deletedIds.has(String(user.id))));
        }

        return failed;
    };

    const handleDeleteUser = async (user) => {
        if (!window.confirm(`Sei sicuro di voler eliminare l'utente ${user.username}?`)) {
            return;
        }

        try {
            const api = getAuthenticatedApi();
            await api.delete(`/users.php?id=${user.id}`);
            setUsers(prev => prev.filter(u => u.id !== user.id));
        } catch (err) {
            console.error('Error deleting user:', err);
            setError('Errore durante l\'eliminazione dell\'utente');
        }
    };

    const handleUserFormSuccess = (newUser) => {
        setUsers(prev => {
            const exists = prev.some(u => u.id === newUser.id);
            if (exists) {
                return prev.map(u => u.id === newUser.id ? { ...u, ...newUser } : u);
            } else {
                return [...prev, newUser];
            }
        });
        setShowUserForm(false);
    };

    // Filter users with safety checks for undefined properties
    const filteredUsers = users.filter(user => {
        if (!user || !user.username) return false;

        const username = user.username || '';
        const email = user.email || '';
        const name = user.name || '';
        const roleName = user.role_name || '';
        const isActive = !!user.active;
        
        const query = (searchQuery || '').toLowerCase();
        const gymName = user.gym_name || '';
        const phone = user.phone || '';
        const matchesSearch =
            username.toLowerCase().includes(query) ||
            email.toLowerCase().includes(query) ||
            (name && name.toLowerCase().includes(query)) ||
            gymName.toLowerCase().includes(query) ||
            phone.toLowerCase().includes(query) ||
            String(user.id).includes(query);

        // Logica di filtraggio ruoli migliorata
        let matchesRole = false;
        
        if (filters.role === 'gym') {
            // Se filtro principale è 'gym', mostra palestre + ruoli opzionali selezionati
            matchesRole = roleName === 'gym' || 
                         (filters.showOptionalRoles.standalone && roleName === 'standalone') ||
                         (filters.showOptionalRoles.trainer && roleName === 'trainer') ||
                         (filters.showOptionalRoles.user && roleName === 'user');
        } else if (filters.role === 'all') {
            // Se 'all', mostra tutti
            matchesRole = true;
        } else {
            // Filtro specifico per ruolo
            matchesRole = roleName === filters.role;
        }
        
        const matchesStatus = filters.status === 'all' ||
            (filters.status === 'active' ? isActive : !isActive);

        return matchesSearch && matchesRole && matchesStatus;
    });

    const handleWorkoutRemoved = (userId, schedaId) => {
        setUsers((prev) => prev.map((user) => {
            if (String(user.id) !== String(userId)) return user;
            return {
                ...user,
                workouts: (user.workouts || []).filter((workout) => {
                    const id = workout.scheda_id || workout.id;
                    return String(id) !== String(schedaId);
                })
            };
        }));
    };

    if (loading && users.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {filters.role === 'gym' ? t('admin.userList.title') : t('admin.userList.userManagement')}
                </h2>

                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setViewMode('verify')}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm ${viewMode === 'verify' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
                        >
                            <Table2 className="h-4 w-4" />
                            {t('admin.userVerify.viewExpert')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode('cards')}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
                        >
                            <LayoutGrid className="h-4 w-4" />
                            {t('admin.userVerify.viewCards')}
                        </button>
                    </div>
                <button
                    onClick={() => setShowUserForm(true)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('admin.userList.newUser')}
                </button>
                </div>
            </div>

            {error && (
                <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    {error}
                </div>
            )}

            {/* Search and filters */}
            <div className="mb-6 space-y-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                        type="text"
                        placeholder={t('admin.userVerify.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                </div>

                <div className="space-y-4">
                    {/* Filtro principale */}
                    <div className="flex flex-wrap gap-4">
                        <div className="flex items-center space-x-2">
                            <Filter className="h-4 w-4 text-gray-500" />
                            <select
                                value={filters.role}
                                onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="gym">{t('admin.userList.filters.gyms')}</option>
                                <option value="all">{t('admin.userList.filters.all')}</option>
                                <option value="admin">{t('admin.userList.filters.onlyAdmins')}</option>
                                <option value="standalone">{t('admin.userList.filters.onlyStandalone')}</option>
                                <option value="trainer">{t('admin.userList.filters.onlyTrainers')}</option>
                                <option value="user">{t('admin.userList.filters.onlyUsers')}</option>
                            </select>
                        </div>

                        <div className="flex items-center space-x-2">
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="all">{t('admin.userList.filters.allStatuses')}</option>
                                <option value="active">{t('admin.userList.filters.active')}</option>
                                <option value="inactive">{t('admin.userList.filters.inactive')}</option>
                            </select>
                        </div>
                    </div>

                    {/* Filtri opzionali - visibili solo quando il filtro principale è 'gym' */}
                    {filters.role === 'gym' && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
                                {t('admin.userList.filters.showAlso')}
                            </h4>
                            <div className="flex flex-wrap gap-4">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={filters.showOptionalRoles.standalone}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            showOptionalRoles: {
                                                ...prev.showOptionalRoles,
                                                standalone: e.target.checked
                                            }
                                        }))}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('admin.userList.filters.standaloneUsers')}</span>
                                </label>
                                
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={filters.showOptionalRoles.trainer}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            showOptionalRoles: {
                                                ...prev.showOptionalRoles,
                                                trainer: e.target.checked
                                            }
                                        }))}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('admin.userList.filters.trainers')}</span>
                                </label>
                                
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={filters.showOptionalRoles.user}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            showOptionalRoles: {
                                                ...prev.showOptionalRoles,
                                                user: e.target.checked
                                            }
                                        }))}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('admin.userList.filters.users')}</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Users grid */}
            {filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {searchQuery || filters.role !== 'gym' || filters.status !== 'all' || 
                     Object.values(filters.showOptionalRoles).some(Boolean)
                        ? 'Nessun utente trovato con i filtri selezionati'
                        : 'Nessuna palestra presente'
                    }
                </div>
            ) : viewMode === 'verify' ? (
                <UserVerifyTable
                    users={filteredUsers}
                    onEdit={onEditUser}
                    onDelete={handleDeleteUser}
                    onDeleteMany={handleDeleteUsers}
                    onWorkoutRemoved={handleWorkoutRemoved}
                    onCreateWorkout={onCreateWorkout}
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredUsers.map(user => (
                        <UserCard
                            key={user.id}
                            user={user}
                            onEdit={() => onEditUser(user)}
                            onDelete={() => handleDeleteUser(user)}
                            onWorkoutRemoved={handleWorkoutRemoved}
                            onCreateWorkout={(targetUser, mode, workout) => onCreateWorkout(targetUser || user, mode, workout)}
                        />
                    ))}
                </div>
            )}

            {/* User form modal */}
            {showUserForm && (
                <UserForm
                    onClose={() => setShowUserForm(false)}
                    onSuccess={handleUserFormSuccess}
                />
            )}
        </div>
    );
};

export default UserList;